// Per-contract file list + upload control (Phase 4).
//
// Replaces the inline file list previously rendered directly inside
// LegalContractDetail. Owns:
//   - Upload (hidden <input type="file"> + drop zone)
//   - Per-file metadata row (icon + name + size + uploaded_by + date)
//   - Per-file actions: download, delete, retry-summary
//   - Per-file <FileSummaryPanel> rendering + polling
//
// All API calls go through ``legalContractService``. Parent owns the
// canonical ``contract`` object; this component asks the parent to
// refresh when the file list mutates (so the global file_count stays
// in sync with anything else on the page).

import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faFile,
  faFilePdf,
  faFileWord,
  faFileImage,
  faTrash,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';

import { legalContractService } from '../../../services/legalContractService';
import FileSummaryPanel from './FileSummaryPanel';

// Upload constraints. Mirrors the backend's
// ``MAX_UPLOAD_SIZE_BYTES`` + ``_ALLOWED_UPLOAD_*`` in serializers.py.
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.doc', '.docx', '.txt'];
const ALLOWED_HINT = 'PDF, DOC, DOCX, PNG, JPG, TXT (max 25 MB)';

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatUploadedAt = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_e) {
    return value;
  }
};

const fileIconFor = (filename) => {
  const lower = (filename || '').toLowerCase();
  if (lower.endsWith('.pdf')) return faFilePdf;
  if (lower.endsWith('.doc') || lower.endsWith('.docx')) return faFileWord;
  if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) {
    return faFileImage;
  }
  return faFile;
};

const validateClientSide = (file) => {
  if (!file) return 'No file selected.';
  if (file.size <= 0) return 'File is empty.';
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `File exceeds the 25 MB limit (got ${formatBytes(file.size)}).`;
  }
  const lower = (file.name || '').toLowerCase();
  const extOk = ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
  if (!extOk) {
    return `Unsupported file type. Allowed: ${ALLOWED_HINT}.`;
  }
  return null;
};

// Forward-ref so the parent (LegalContractDetail) can drive the file
// picker from its floating-action-menu without lifting the upload
// state out of this component. ``openFilePicker()`` triggers a click
// on the hidden ``<input type="file">``; ``isUploading()`` lets the
// parent reflect upload state in the FAB if it ever wants to. The
// ``hideUploadButton`` prop lets the parent suppress the inline
// "Upload file" button when it's owning that action from the FAB.
// ``canEdit`` gates the mutating affordances (upload + delete + retry):
// a Legal Approver gets a read + download view only, matching the backend
// which rejects their file calls regardless. Defaults to true so existing
// full-access callers are unaffected.
const ContractFileList = forwardRef((
  { contract, onContractChange, hideUploadButton = false, canEdit = true },
  ref,
) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const files = Array.isArray(contract?.files) ? contract.files : [];
  const contractId = contract?.id;

  useImperativeHandle(ref, () => ({
    openFilePicker: () => {
      if (uploading) return;
      fileInputRef.current && fileInputRef.current.click();
    },
    isUploading: () => uploading,
  }), [uploading]);

  const handleFilePicked = async (event) => {
    const picked = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!picked) return;

    const validationError = validateClientSide(picked);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setUploading(true);
    setUploadProgress(0);
    try {
      await legalContractService.uploadFile(
        contractId,
        picked,
        (progressEvent) => {
          if (progressEvent && progressEvent.total) {
            const pct = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setUploadProgress(pct);
          }
        },
      );
      // Re-fetch the full contract via the parent so the new file row
      // (and its analysis FK) lands with consistent state.
      if (onContractChange) await onContractChange();
    } catch (err) {
      setError(
        err?.response?.data?.file?.[0]
          || err?.response?.data?.detail
          || err?.message
          || 'Upload failed.',
      );
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (fileId) => {
    if (pendingDeleteId === fileId) return;
    // Lightweight confirmation — no modal — to match the rest of the
    // legal module's minimal UI surface in v1.
    // eslint-disable-next-line no-alert
    const ok = window.confirm('Delete this file? This cannot be undone.');
    if (!ok) return;
    setPendingDeleteId(fileId);
    setError(null);
    try {
      await legalContractService.deleteFile(contractId, fileId);
      if (onContractChange) await onContractChange();
    } catch (err) {
      setError(
        err?.response?.data?.detail
          || err?.message
          || 'Delete failed.',
      );
    } finally {
      setPendingDeleteId(null);
    }
  };

  const handleDownload = async (file) => {
    setError(null);
    try {
      const blob = await legalContractService.downloadFile(contractId, file.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename || `contract-${contractId}-${file.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Give the browser a moment to start the download before revoking
      // the object URL.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(
        err?.response?.data?.detail
          || err?.message
          || 'Download failed.',
      );
    }
  };

  return (
    <div className="legal-files">
      <div className="legal-files__header">
        {canEdit && !hideUploadButton && (
          <button
            type="button"
            className="legal-files__upload-btn"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={uploading}
          >
            <FontAwesomeIcon icon={faUpload} />
            {uploading ? `Uploading ${uploadProgress}%` : 'Upload file'}
          </button>
        )}
        <span className="legal-files__hint">
          {uploading && hideUploadButton
            ? `Uploading ${uploadProgress}%`
            : (canEdit ? ALLOWED_HINT : 'Download only — contact your administrator to edit files.')}
        </span>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt"
          onChange={handleFilePicked}
          style={{ display: 'none' }}
        />
      </div>

      {error && (
        <div className="legal-files__error" role="alert">{error}</div>
      )}

      {files.length === 0 ? (
        <div className="legal-detail-files-empty">
          <FontAwesomeIcon icon={faFile} />
          <span>No files uploaded yet.</span>
          <small>Drop a contract PDF or supporting document above.</small>
        </div>
      ) : (
        <ul className="legal-detail-files">
          {files.map((file) => (
            <li key={file.id} className="legal-detail-file">
              <FontAwesomeIcon icon={fileIconFor(file.original_filename)} />
              <div className="legal-detail-file__main">
                <div className="legal-detail-file__name" title={file.original_filename}>
                  {file.original_filename}
                </div>
                <div className="legal-detail-file__meta">
                  {formatBytes(file.size_bytes)}
                  {' • '}
                  {file.uploaded_by_name || '—'}
                  {' • '}
                  {formatUploadedAt(file.uploaded_at)}
                  {file.folder && ` • Folder: ${file.folder.label}`}
                </div>
                <FileSummaryPanel contractId={contractId} file={file} canEdit={canEdit} />
              </div>
              <div className="legal-detail-file__actions">
                <button
                  type="button"
                  className="legal-detail-file__action"
                  onClick={() => handleDownload(file)}
                  title="Download"
                  aria-label={`Download ${file.original_filename}`}
                >
                  <FontAwesomeIcon icon={faDownload} />
                </button>
                {canEdit && (
                  <button
                    type="button"
                    className="legal-detail-file__action legal-detail-file__action--danger"
                    onClick={() => handleDelete(file.id)}
                    disabled={pendingDeleteId === file.id}
                    title="Delete"
                    aria-label={`Delete ${file.original_filename}`}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

ContractFileList.displayName = 'ContractFileList';

export default ContractFileList;
